import { BehaviorSubject, debounceTime, map } from "rxjs";
import { LoadingTask } from "./models";
import { v4 as uuid } from "uuid";

export class LoadingManager {
  private tasks$ = new BehaviorSubject<LoadingTask[]>([]);

  /**
   * Current task to display
   */
  readonly currentTask$ = this.tasks$
    .asObservable()
    .pipe(map((tasks) => (tasks[0] ?? null) as LoadingTask | null));

  /**
   * Whether to show the loading screen or not
   */
  readonly showLoadingScreen$ = this.currentTask$.pipe(
    map((task) => Boolean(task)),
    debounceTime(300)
  );
  readonly shouldRenderLoadingScreen$ = new BehaviorSubject(true);
  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(private config: { transitionTimeMs: number }) {
    this.showLoadingScreen$.subscribe((showLoadingScreen) => {
      // Clear old timeout
      if (this.timeoutId !== null) {
        clearTimeout(this.timeoutId);
      }
      // If need to show, set to true immediately
      if (showLoadingScreen) {
        this.shouldRenderLoadingScreen$.next(true);
        return;
      }
      // Change render value after a while
      this.timeoutId = setTimeout(() => {
        this.shouldRenderLoadingScreen$.next(false);
      }, this.config.transitionTimeMs);
    });
  }

  /**
   * Adds a task to load. If id is specified, it will replace any task with
   * that id
   */
  addTask(meta: LoadingTask["meta"], id: string = uuid()) {
    const newTask: LoadingTask = {
      id,
      meta,
    };
    this.tasks$.next([
      ...this.tasks$.getValue().filter((task) => task.id !== id),
      newTask,
    ]);
    return newTask.id;
  }

  /**
   * Updates the state of a task
   */
  updateTask(
    id: string,
    transformer: (meta: LoadingTask["meta"]) => LoadingTask["meta"]
  ) {
    this.tasks$.next(
      this.tasks$
        .getValue()
        .map((task) =>
          task.id === id ? { ...task, meta: transformer(task.meta) } : task
        )
    );
  }

  /**
   * Ends a loading task
   */
  endTask(id: string) {
    this.tasks$.next(this.tasks$.getValue().filter((task) => task.id !== id));
  }
}
